import { useMemo } from 'react';
import { useApiGet, useApiPost, useApiPut, MutationOptions, PostRequestParams, PutRequestParams } from '../../../../../services/tanstackApiService';
import i18n from '../../../../../services/i18n';

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Package info from the API response
 */
export interface PackageInfo {
  id: number;
  package_name: string;
  code: string;
  package_amount: number;
  concession_amount: number;
  net_amount: number;
  service_amount: number;
  pharmacy_amount: number;
  is_finalized: boolean;
  start_date: string;
  valid_until: string;
  advance_paid: number;
  advance_balance_amount: number;
  service_consumed: number;
  service_pending: number;
  pharmacy_consumed: number;
  pharmacy_pending: number;
  total_service_concession_amount: number;
  service_net_amount: number;
}

/**
 * Service item from the API response
 */
export interface ServiceItem {
  service_name: string;
  procedure_name: string;
  gender: string;
  discount_percent: number;
  unit_rate: number;
  quantity: number;
  used: number;
  pending: number;
  unlimited_quantity: boolean;
  adjustable_head: boolean;
  adjustable_head_type: string | null;
  service_code: string;
  service_source_type: string;
  consumed_amount: number;
  is_billed: boolean;
}

/**
 * Pharmacy item from the API response
 */
export interface PharmacyItem {
  item_name: string;
  item_code: string;
  quantity: number;
  consumed: number;
  pending: number;
  uom: string;
  unit_price: number;
  discount_percent: number;
  consumed_amount: number;
  is_billed: boolean;
  procedure_name?: string | null;
}

/**
 * Full API response for package consumption details
 */
export interface PackageConsumptionDetailsResponse {
  package_info: PackageInfo;
  services: ServiceItem[];
  pharmacy: PharmacyItem[];
}

// ============================================================================
// Report API Types
// ============================================================================

/**
 * Patient info for report
 */
export interface ReportPatientInfo {
  name: string;
  age: number;
  date_of_birth: string;
  gender: string;
  contact_no: string;
  mr_no: string;
}

/**
 * Partner info for report
 */
export interface ReportPartnerInfo {
  name: string;
  age: number;
  date_of_birth: string;
  gender: string;
  mr_no: string;
}

/**
 * Package info for report
 */
export interface ReportPackageInfo {
  id: number;
  name: string;
  code: string;
  package_amount: number;
  concession_amount: number;
  net_amount: number;
  service_amount: number;
  pharmacy_amount: number;
  is_finalized: boolean;
  start_date: string;
  valid_until: string;
  advance_paid: number;
  advance_balance_amount: number;
  service_consumed: number;
  service_pending: number;
  pharmacy_consumed: number;
  pharmacy_pending: number;
  total_service_concession_amount: number;
  service_net_amount: number;
}

/**
 * Service item for report
 */
export interface ReportServiceItem {
  sr_no: number;
  date: string;
  invoice_id: number;
  bill_number: string | null;
  visit_id: number;
  opd_ipd_no: string;
  service_code: string;
  service_name: string;
  service_type: string;
  prescribed_by: string;
  charges: number;
  concession_amount: number;
  discount_amount: number;
  tax_amount: number;
  net_amount: number;
  is_refunded: boolean;
  refund_reason: string;
}

/**
 * Pharmacy item for report
 */
export interface ReportPharmacyItem {
  sr_no: number;
  date: string;
  invoice_id: number;
  bill_number: string | null;
  visit_id: number;
  opd_ipd_no: string;
  item_code: string;
  item_name: string;
  batch_number: string;
  expiry_date: string;
  prescribed_quantity: number;
  billed_quantity: number;
  prescribed_uom: string;
  billed_uom: string;
  unit_price: number;
  base_mrp: number;
  discount_amount: number;
  tax_amount: number;
  net_amount: number;
  dispensed_by: string;
}

/**
 * Full API response for package consumption report
 */
export interface PackageConsumptionReportResponse {
  patient_info: ReportPatientInfo;
  partner_info: ReportPartnerInfo | null;
  package_info: ReportPackageInfo;
  services: ReportServiceItem[];
  pharmacy: ReportPharmacyItem[];
}

/**
 * Consumption item type - can be a service or pharmacy item
 */
export type ConsumptionItemType = 'SERVICE' | 'PHARMACY';

/**
 * Individual consumption record for tracking usage history
 */
export interface ConsumptionRecord {
  id: number;
  consumption_date: string;
  item_type: ConsumptionItemType;
  quantity_consumed: number;
  prescribing_doctor_id?: number | null;
  prescribing_doctor_name?: string | null;
  notes?: string;
  created_on: string;
  created_by?: number;
}

/**
 * Package consumption item - represents a service or pharmacy item mapped to a package
 */
export interface PackageConsumptionItem {
  id: number;
  package_patient_id: number;
  item_id: number;
  item_name: string;
  procedure_name?: string | null;
  item_type: ConsumptionItemType;
  gender?: string | null;
  discount_percent: number;
  unit_rate: number;
  total_quantity: number;
  availed_quantity: number;
  pending_quantity: number;
  unlimited_quantity?: boolean;
  adjustable_head?: boolean;
  adjustable_head_type?: string | null;
  uom?: string | null;
  is_selected: boolean;
  is_confirmed: boolean;
  consumption_history: ConsumptionRecord[];
  created_on?: string;
  updated_on?: string;
}

/**
 * API response for fetching package consumption items
 */
export interface PackageConsumptionResponse {
  success: boolean;
  data: PackageConsumptionItem[];
  meta?: {
    total_count: number;
    confirmed_count: number;
    pending_confirmation_count: number;
  };
}

/**
 * Request payload for confirming selected items
 */
export interface ConfirmSelectionRequest {
  package_patient_id: number;
  item_ids: number[];
}

/**
 * Response for confirmation action
 */
export interface ConfirmSelectionResponse {
  success: boolean;
  message: string;
  data?: PackageConsumptionItem[];
}

/**
 * Request payload for recording consumption
 */
export interface RecordConsumptionRequest {
  package_patient_id: number;
  item_id: number;
  item_type: ConsumptionItemType;
  quantity_consumed: number;
  prescribing_doctor_id?: number | null;
  notes?: string;
}

/**
 * Response for recording consumption
 */
export interface RecordConsumptionResponse {
  success: boolean;
  message: string;
  data?: {
    consumption_record: ConsumptionRecord;
    updated_item: PackageConsumptionItem;
    stock_sync_status: 'success' | 'failed' | 'pending';
  };
}

/**
 * Request for syncing with stock/material system
 */
export interface StockSyncRequest {
  item_id: number;
  item_type: ConsumptionItemType;
  quantity: number;
  operation: 'DEDUCT' | 'RESTORE';
  reference_id: number; // consumption record id
}

/**
 * Stock sync response
 */
export interface StockSyncResponse {
  success: boolean;
  message: string;
  new_stock_level?: number;
}

/**
 * Update selection request
 */
export interface UpdateSelectionRequest {
  package_patient_id: number;
  selections: Array<{
    item_id: number;
    is_selected: boolean;
  }>;
}

/**
 * Update selection response
 */
export interface UpdateSelectionResponse {
  success: boolean;
  message: string;
  data?: PackageConsumptionItem[];
}

// ============================================================================
// API URL Helpers
// ============================================================================

export function getPackageConsumptionURL(packagePatientId: number): string {
  return `patient-packages/${packagePatientId}/consumption/`;
}

export function confirmSelectionURL(): string {
  return 'patient-packages/consumption/confirm/';
}

export function recordConsumptionURL(): string {
  return 'patient-packages/consumption/record/';
}

export function updateSelectionURL(): string {
  return 'patient-packages/consumption/selection/';
}

export function stockSyncURL(): string {
  return 'inventory/stock-sync/';
}

export function getPackageConsumptionDetailsURL(clinicId: number, packageId: number): string {
  return `patient-reports/package-consumption-details/?clinic_id=${clinicId}&package_id=${packageId}`;
}

export function getPackageConsumptionReportURL(clinicId: number, packageId: number): string {
  return `patient-reports/package-consumption-report/?clinic_id=${clinicId}&package_id=${packageId}`;
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to fetch package consumption report
 * GET /patient-reports/package-consumption-report/?clinic_id={clinicId}&package_id={packageId}
 */
export function usePackageConsumptionReport(clinicId?: number | null, packageId?: number | null, enabled?: boolean) {
  const isEnabled = enabled !== false && !!clinicId && !!packageId;
  
  return useApiGet<PackageConsumptionReportResponse>(
    ['package-consumption-report', clinicId ?? 0, packageId ?? 0],
    {
      path: isEnabled ? getPackageConsumptionReportURL(clinicId!, packageId!) : '',
    },
    {
      enabled: isEnabled,
      staleTime: 30_000,
      gcTime: 300_000,
    }
  );
}

/**
 * Hook to fetch package consumption details
 * GET /patient-reports/package-consumption-details/?clinic_id={clinicId}&package_id={packageId}
 */
export function usePackageConsumptionDetails(clinicId?: number | null, packageId?: number | null) {
  const enabled = !!clinicId && !!packageId;
  
  return useApiGet<PackageConsumptionDetailsResponse>(
    ['package-consumption-details', clinicId ?? 0, packageId ?? 0],
    {
      path: enabled ? getPackageConsumptionDetailsURL(clinicId!, packageId!) : '',
    },
    {
      enabled,
      staleTime: 30_000,
      gcTime: 300_000,
    }
  );
}

/**
 * Hook to fetch package consumption items
 * GET /patient-packages/{packagePatientId}/consumption/
 */
export function usePackageConsumption(packagePatientId?: number | null) {
  return useApiGet<PackageConsumptionResponse>(
    ['package-consumption', packagePatientId ?? 0],
    {
      path: packagePatientId ? getPackageConsumptionURL(packagePatientId) : '',
    },
    {
      enabled: !!packagePatientId,
      staleTime: 0,
      gcTime: 0,
    }
  );
}

/**
 * Convenience hook that returns just the items array
 */
export function usePackageConsumptionItems(packagePatientId?: number | null) {
  const { data, isLoading, isError, refetch, isFetching } = usePackageConsumption(packagePatientId);

  const items: PackageConsumptionItem[] = useMemo(
    () => data?.data ?? [],
    [data]
  );

  const meta = useMemo(() => data?.meta, [data]);

  return {
    items,
    meta,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
}

/**
 * Hook for confirming selected items
 * POST /patient-packages/consumption/confirm/
 */
export function useConfirmSelection(
  options?: MutationOptions<ConfirmSelectionResponse, PostRequestParams<ConfirmSelectionRequest>>
) {
  return useApiPost<ConfirmSelectionResponse, ConfirmSelectionRequest>({
    ...options,
  });
}

/**
 * Hook for recording consumption
 * POST /patient-packages/consumption/record/
 */
export function useRecordConsumption(
  options?: MutationOptions<RecordConsumptionResponse, PostRequestParams<RecordConsumptionRequest>>
) {
  return useApiPost<RecordConsumptionResponse, RecordConsumptionRequest>({
    ...options,
  });
}

/**
 * Hook for updating item selections
 * PUT /patient-packages/consumption/selection/
 */
export function useUpdateSelection(
  options?: MutationOptions<UpdateSelectionResponse, PutRequestParams<UpdateSelectionRequest>>
) {
  return useApiPut<UpdateSelectionResponse, UpdateSelectionRequest>({
    ...options,
  });
}

/**
 * Hook for syncing with stock/material system
 * POST /inventory/stock-sync/
 */
export function useStockSync(
  options?: MutationOptions<StockSyncResponse, PostRequestParams<StockSyncRequest>>
) {
  return useApiPost<StockSyncResponse, StockSyncRequest>({
    ...options,
  });
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build confirm selection request payload
 */
export function buildConfirmSelectionRequest(
  packagePatientId: number,
  selectedItemIds: number[]
): ConfirmSelectionRequest {
  return {
    package_patient_id: packagePatientId,
    item_ids: selectedItemIds,
  };
}

/**
 * Build record consumption request payload
 */
export function buildRecordConsumptionRequest(params: {
  packagePatientId: number;
  itemId: number;
  itemType: ConsumptionItemType;
  quantityConsumed: number;
  prescribingDoctorId?: number | null;
  notes?: string;
}): RecordConsumptionRequest {
  return {
    package_patient_id: params.packagePatientId,
    item_id: params.itemId,
    item_type: params.itemType,
    quantity_consumed: params.quantityConsumed,
    prescribing_doctor_id: params.prescribingDoctorId,
    notes: params.notes,
  };
}

/**
 * Check if consumption is allowed (pending quantity > 0)
 */
export function canRecordConsumption(item: PackageConsumptionItem, quantity: number): boolean {
  return item.is_confirmed && item.pending_quantity >= quantity && quantity > 0;
}

/**
 * Get consumption error message
 */
export function getConsumptionErrorMessage(
  item: PackageConsumptionItem,
  requestedQuantity: number
): string | null {
  if (!item.is_confirmed) {
    return i18n.t('consumption_details.errors.item_not_confirmed', {
      ns: 'Packagebilling',
      defaultValue: 'Item must be confirmed before recording consumption.',
    });
  }
  if (requestedQuantity <= 0) {
    return i18n.t('consumption_details.errors.quantity_gt_zero', {
      ns: 'Packagebilling',
      defaultValue: 'Quantity must be greater than 0.',
    });
  }
  if (item.pending_quantity < requestedQuantity) {
    return i18n.t('consumption_details.errors.insufficient_pending', {
      ns: 'Packagebilling',
      available: item.pending_quantity,
      requested: requestedQuantity,
      defaultValue: 'Insufficient pending quantity. Available: {{available}}, Requested: {{requested}}',
    });
  }
  return null;
}

/**
 * Format date for display
 */
export function formatConsumptionDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

/**
 * Calculate totals for the consumption table
 */
export function calculateConsumptionTotals(items: PackageConsumptionItem[]) {
  return items.reduce(
    (acc, item) => ({
      totalQuantity: acc.totalQuantity + item.total_quantity,
      totalAvailed: acc.totalAvailed + item.availed_quantity,
      totalPending: acc.totalPending + item.pending_quantity,
      totalValue: acc.totalValue + item.unit_rate * item.total_quantity,
    }),
    { totalQuantity: 0, totalAvailed: 0, totalPending: 0, totalValue: 0 }
  );
}

/**
 * Filter items by type
 */
export function filterItemsByType(
  items: PackageConsumptionItem[],
  type: ConsumptionItemType | 'ALL'
): PackageConsumptionItem[] {
  if (type === 'ALL') return items;
  return items.filter((item) => item.item_type === type);
}

/**
 * Sort items by confirmation status (confirmed first) then by name
 */
export function sortConsumptionItems(items: PackageConsumptionItem[]): PackageConsumptionItem[] {
  return [...items].sort((a, b) => {
    // Confirmed items first
    if (a.is_confirmed !== b.is_confirmed) {
      return a.is_confirmed ? -1 : 1;
    }
    // Then by name
    return a.item_name.localeCompare(b.item_name);
  });
}